/**
 * The formatted, render-ready view of one sim state. Pulling this out of the
 * Solid component keeps the HUD a dumb renderer of strings and makes every
 * gauge reading assertable in a plain unit test.
 */

import { headingAngle, metresToFeet, pilotMayBailOut, type SimState } from "@red-opus/engine";
import {
  formatAirspeedMph,
  formatAltitudeFt,
  formatHeading,
  formatPercent,
  formatRounds,
  groundCallout,
  headingCardinal,
} from "./hud-format.ts";
import { fuelFraction, SIM_EVENT_MESSAGES } from "./sim-messages.ts";

/**
 * The one line the HUD's centre-top slot carries — **empty for an aeroplane
 * that is simply flying**.
 *
 * It used to carry the original's attitude callout (`CLIMBING.` /
 * `DESCENDING.` / `FLYING LEVEL.`, verbatim from the `RB.EXE` string dump), and
 * therefore carried *something* on every frame of every flight. The user's
 * verdict on 2026-08-26 was that this is chatter in the most prominent place
 * the HUD has, and the altimeter two inches away already says it with a number
 * that does not have to be read as a sentence.
 *
 * What is left is the two things the slot is worth having for, in priority
 * order. A stall is a standing condition and gets a standing warning, in the
 * original's own words (`SIM_EVENT_MESSAGES.stallEntered` — the same string the
 * message feed announces the *edge* with, taken from there rather than copied,
 * so the two can never drift apart). Wheels down gets `groundCallout`, which is
 * a port-side line because the dump has none. Everything else gets silence.
 *
 * The composition lives here rather than in `hud-format.ts` because
 * `SIM_EVENT_MESSAGES` does: `sim-messages.ts` reaches `hud-format.ts` through
 * `combat-hud.ts`, so importing it the other way would close a cycle.
 */
export function flightCallout(state: SimState): string {
  if (state.readout.grounded) return groundCallout(state.gear);
  return state.readout.stalled ? SIM_EVENT_MESSAGES.stallEntered : "";
}

export interface FlightHud {
  readonly airspeedMph: string;
  readonly altitudeFt: string;
  readonly heading: string;
  readonly cardinal: string;
  readonly throttle: string;
  readonly fuel: string;
  readonly rounds: string;
  /** The centre-top line, or `""` when the aeroplane has nothing worth saying — see `flightCallout`. */
  readonly callout: string;
  readonly stalled: boolean;
  readonly jammed: boolean;
  /**
   * Mid-burst right now — drives the cockpit's muzzle-flash variant (the
   * dash sheet's frame 1, which the per-aircraft `.TTM`s draw at each gun's
   * muzzle in their firing blocks). `guns.burstRounds` is the engine's own
   * "rounds fired in the current uninterrupted burst" and `fireBurst` resets
   * it to 0 the moment the trigger is released, so a positive value is
   * exactly "the guns are going".
   */
  readonly firing: boolean;
  readonly outOfFuel: boolean;
  readonly crashed: boolean;
  /**
   * AD-021: the pilot has left the aeroplane. On the HUD snapshot because the
   * route needs it and has no other window onto the sim — the camera chip must
   * not go on saying COCKPIT VIEW once there is no cockpit to sit in
   * (`scene-camera.ts`'s `effectiveCameraMode` makes the same call for the
   * camera itself, and the chip has to agree with it).
   */
  readonly baledOut: boolean;
  /**
   * AD-021 again, and the other way round: whether the pilot *could* go over the
   * side right now. Here for the same reason `baledOut` is — the route has no
   * other window onto the sim — and specifically so G38's in-flight `ESC` dialog
   * can decide whether to draw its BAIL OUT row at all.
   *
   * **Not a second copy of the rules**: it is the engine's own
   * `pilotMayBailOut`, the predicate `bailOut` itself refuses on. The `H` hold
   * still asks nothing and fires blind; a row that can be clicked has to know.
   */
  readonly bailAvailable: boolean;
  /** Wheels on the surface. */
  readonly onGround: boolean;
  readonly gearCollapsed: boolean;
  /** Height of the wheels above the terrain, in feet — what a landing is flown on. */
  readonly altitudeAglFt: string;
  readonly timeS: number;
  /**
   * Raw (unformatted) readings the cockpit panel's needle math wants full
   * precision for — a formatted string like `altitudeFt` above is already
   * rounded for display and would make a needle visibly step. Added for the
   * cockpit instrument panel (`instrument-values.ts`); every field here has
   * an existing formatted twin above for the digital HUD.
   */
  readonly raw: {
    readonly airspeedMps: number;
    readonly altitudeFt: number;
    readonly headingRad: number;
    /** Bank (roll) angle, radians — positive = right wing down. Drives the turn indicator. */
    readonly bankRad: number;
    readonly throttleFraction: number;
    readonly fuelFraction: number;
    /** 0..1, `damage.engine` — 1 is undamaged. Drives the oil gauge (INFERRED mapping, see instrument-values.ts). */
    readonly engineHealthFraction: number;
  };
}

export function hudFrom(state: SimState): FlightHud {
  return {
    airspeedMph: formatAirspeedMph(state.readout.airspeedMps),
    altitudeFt: formatAltitudeFt(metresToFeet(state.position.y)),
    heading: formatHeading(headingAngle(state.orientation)),
    cardinal: headingCardinal(headingAngle(state.orientation)),
    throttle: formatPercent(state.engine.throttle),
    fuel: formatPercent(fuelFraction(state)),
    rounds: formatRounds(state.guns.rounds),
    callout: flightCallout(state),
    stalled: state.readout.stalled,
    jammed: state.guns.jammed,
    firing: state.guns.burstRounds > 0,
    outOfFuel: state.engine.fuelExhausted,
    crashed: state.crashed,
    baledOut: state.pilot.bailedOut,
    bailAvailable: pilotMayBailOut(state),
    onGround: state.readout.grounded,
    gearCollapsed: state.gear.collapsed,
    altitudeAglFt: formatAltitudeFt(metresToFeet(state.readout.altitudeAglM)),
    timeS: state.timeS,
    raw: {
      airspeedMps: state.readout.airspeedMps,
      altitudeFt: metresToFeet(state.position.y),
      headingRad: headingAngle(state.orientation),
      bankRad: state.readout.bankRad,
      throttleFraction: state.engine.throttle,
      fuelFraction: fuelFraction(state),
      engineHealthFraction: state.damage.engine,
    },
  };
}
